import Spinner from '@components/spinner/Spinner';
import { useFetchTranscriptSummary, useRegenerateTranscriptSummary } from '@hooks/getQueries';
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Markdown from 'react-markdown';

// Plain-language definitions shown on hover for each coverage metric.
const COVERAGE_HINTS = {
  Asked: 'Questions actually put to the participant during the interview.',
  Answered: 'Questions the participant gave a substantive response to.',
  Unanswered:
    'Questions that were asked but got no participant response (e.g. the interview ended right after).',
  Deferred: 'Questions the participant explicitly declined or refused to answer.',
  'Not asked': 'Main questions from the interview guide that were never asked.',
  Total:
    'Total main questions and probes the researcher planned in the interview guide (the source of truth).',
};

// Compact stat cell for the question-coverage card. When the label has a hint,
// it becomes a dotted-underlined, hoverable definition.
const CoverageStat = ({ label, value }) => {
  const hint = COVERAGE_HINTS[label];
  const labelNode = (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={
        hint
          ? { borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }
          : undefined
      }
    >
      {label}
    </Typography>
  );
  return (
    <Box sx={{ minWidth: 92 }}>
      <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
        {value}
      </Typography>
      {hint ? (
        <Tooltip title={hint} arrow placement="top">
          {labelNode}
        </Tooltip>
      ) : (
        labelNode
      )}
    </Box>
  );
};

const CoverageRow = ({ label, bucket, showNotAsked = false, emphasize = false }) => (
  <Box display="flex" alignItems="center" gap={3} flexWrap="wrap" py={0.75}>
    <Typography variant="subtitle2" sx={{ minWidth: 130, fontWeight: emphasize ? 700 : 600 }}>
      {label}
    </Typography>
    <CoverageStat label="Asked" value={bucket.asked ?? 0} />
    <CoverageStat label="Answered" value={bucket.answered ?? 0} />
    <CoverageStat label="Unanswered" value={bucket.unanswered ?? 0} />
    <CoverageStat label="Deferred" value={bucket.deferred ?? 0} />
    {showNotAsked && <CoverageStat label="Not asked" value={bucket.not_asked ?? 0} />}
  </Box>
);

const QuestionCoverage = ({ coverage }) => {
  const main = coverage?.main_questions;
  const probes = coverage?.probes;
  if (!main && !probes) return null;

  // Total = what the researcher PLANNED in the interview guide (the source of
  // truth), independent of the transcript. Fallback keeps older summaries
  // (generated before planned_* existed) from showing blanks.
  const plannedMain = coverage?.planned_main ?? (main?.asked ?? 0) + (main?.not_asked ?? 0);
  const plannedProbes = coverage?.planned_probes ?? 0;
  const plannedTotal = plannedMain + plannedProbes;

  return (
    <Card variant="outlined" sx={{ p: 0, pb: 2, width: '100%', mt: 2 }}>
      <Box display="flex" px={2} py={1.3} alignItems="center" gap={1}>
        <Icon icon="fluent:chat-help-24-regular" width="24" height="24" />
        <Typography variant="subtitle2">Question coverage</Typography>
      </Box>
      <Divider />
      <Box p={2}>
        {main && <CoverageRow label="Main questions" bucket={main} showNotAsked />}
        {probes && <CoverageRow label="Probes" bucket={probes} />}
        <Divider sx={{ my: 1 }} />
        <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap" pt={0.5}>
          <Typography variant="subtitle2" sx={{ minWidth: 130, fontWeight: 700 }}>
            Total (guide)
          </Typography>
          <Tooltip title={COVERAGE_HINTS.Total} arrow placement="top">
            <Typography variant="h6" sx={{ lineHeight: 1.1, cursor: 'help' }}>
              {plannedTotal}
            </Typography>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {plannedMain} main + {plannedProbes} probes planned by the researcher
          </Typography>
        </Box>
      </Box>
      {coverage.note && (
        <Typography variant="body2" color="text.secondary" px={2}>
          {coverage.note}
        </Typography>
      )}
    </Card>
  );
};

const TranscriptSummary = ({ transcriptId, transcriptName, enabled = true }) => {
  const { data: transcriptSummary, isLoading } = useFetchTranscriptSummary({
    transcriptId,
    enabled,
  });
  const { mutate: regenerate, isPending: isRegenerating } = useRegenerateTranscriptSummary({
    transcriptId,
  });

  if (isLoading) {
    return (
      <Box
        p={4}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={2}
      >
        <Spinner />
        <Typography variant="body2" color="text.secondary">
          Generating transcript summary... This may take a few seconds.
        </Typography>
      </Box>
    );
  }

  if (!transcriptSummary?.summary) {
    return (
      <Box p={2} m={2} display="flex" justifyContent="center">
        Transcript summary is not available right now.
      </Box>
    );
  }

  return (
    <>
      <Card variant="outlined" sx={{ p: 0, pb: 2, width: '100%', mt: 1 }}>
        <Box display="flex" px={2} py={1.3} justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">{transcriptName} Summary</Typography>
          <Tooltip title={isRegenerating ? 'Regenerating…' : 'Regenerate summary'} arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => regenerate()}
                disabled={isRegenerating}
                aria-label="Regenerate summary"
              >
                {isRegenerating ? (
                  <CircularProgress size={18} />
                ) : (
                  <Icon icon="fluent:arrow-sync-24-regular" width="20" height="20" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Divider />
        <Box p={2} sx={{ '& p': { m: 0, mb: 1 }, '& strong': { fontWeight: 600 } }}>
          <Markdown>{transcriptSummary.summary.summary}</Markdown>
        </Box>
      </Card>

      <QuestionCoverage coverage={transcriptSummary.summary.question_coverage} />

      <Card variant="outlined" sx={{ p: 0, pb: 2, width: '100%', mt: 2 }}>
        <Box display="flex" px={2} py={1.3} justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" display="flex" alignItems="center" gap={1}>
            <Icon icon="fluent:key-multiple-24-regular" width="24" height="24" />
            Key Takeaways
          </Typography>
        </Box>
        <Divider />
        <Box p={2} mt={-2}>
          {transcriptSummary.summary.answers?.length > 0 ? (
            transcriptSummary.summary.answers.map((item, index) => (
              <Box key={`${item.question}-${index}`} mt={2}>
                <Typography variant="subtitle2">
                  {index + 1}. {item.question}
                </Typography>
                <Typography my={0.5}>{item.insight}</Typography>

                {item.aha && (
                  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                    Aha: {item.aha}
                  </Typography>
                )}

                {item.quotes?.length > 0 && (
                  <Box>
                    <Typography my={1} mb={0.6}>
                      <strong>Supporting Quotes</strong>
                    </Typography>
                    {item.quotes.map((quote, quoteIndex) => (
                      <Box key={`${quoteIndex}-${quote}`} sx={{ mb: 0.5, pl: 2 }}>
                        <Typography variant="body2" fontStyle="italic">
                          {quote}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" mt={2}>
              No focused takeaways were generated for this transcript.
            </Typography>
          )}
        </Box>
      </Card>
    </>
  );
};

export default TranscriptSummary;
